import React, { useEffect, useState } from 'react';
import api from '../lib/adminApi';
import { dateTime, endpoint, humanize, message } from './api';
import { LoadState, Notice, Status } from './shared';

export const flowEndpoint = `${endpoint}/flows`;
export type FlowHealthData = {
  asOf: string;
  window: { from: string; to: string };
  funnel: {
    started: number; processing: number; completed: number; failed: number; cancelled: number;
    p50ProcessingMs: number | null; p95ProcessingMs: number | null; processingSampleCount: number;
  };
  flows?: { total: number; items: Array<{
    flowId: string; kind: string; state: string; durationMs: number; processingMs: number | null;
    stageDurationsMs: Record<string, number>;
  }> };
  watchdog: {
    running: boolean; degraded: boolean; intervalMs: number;
    lastSweepAt: string | null; lastSweepError: string | null;
  };
  alerts: {
    total: number;
    items: Array<{
      eventId: string; timestamp: string; kind: string; reason: string; step: string; state: string;
      flowId: string; billId: string | null; userId: string | null; version: string; ageMs: number;
      resolvedAt?: string;
    }>;
  };
};
const duration = (value: number | null) => value === null || !Number.isFinite(value)
  ? 'Not recorded' : `${(value / 1000).toFixed(1)}s`;

export default function FlowHealth({ refresh }: { refresh: number }) {
  const [poll, setPoll] = useState(0);
  const [state, setState] = useState<{ data?: FlowHealthData; loading: boolean; error?: string }>({ loading: true });
  useEffect(() => {
    const timer = window.setInterval(() => setPoll(value => value + 1), 30000);
    return () => window.clearInterval(timer);
  }, []);
  useEffect(() => {
    const controller = new AbortController();
    setState(previous => ({ data: previous.data, loading: !previous.data }));
    // The flow endpoint bypasses the operations snapshot cache and sends no-store.
    api.get(flowEndpoint, { params: { limit: 10, includeTestData: false }, signal: controller.signal, timeout: 10000 })
      .then(response => {
        if (!controller.signal.aborted) setState({ data: response.data, loading: false });
      })
      .catch(error => {
        if (!controller.signal.aborted) setState({ loading: false, error: message(error) });
      });
    return () => controller.abort();
  }, [refresh, poll]);
  const data = state.data;
  const lastSweep = data?.watchdog.lastSweepAt ? Date.parse(data.watchdog.lastSweepAt) : NaN;
  const stale = !!data && (!Number.isFinite(lastSweep)
    || Date.parse(data.asOf) - lastSweep > data.watchdog.intervalMs * 3);
  const degraded = !!data && (data.watchdog.degraded || !data.watchdog.running || stale);
  return (
    <section className="admin-panel" aria-labelledby="flow-health-title">
      <div className="admin-section-heading">
        <div>
          <h2 id="flow-health-title">Bill and receipt flows</h2>
          <p>Last 24 hours · Operational completion records · Excludes test data · Refreshes every 30 seconds</p>
        </div>
      </div>
      <LoadState loading={state.loading} error={state.error ? `Flow health unavailable. ${state.error}` : undefined}
        retry={() => setPoll(value => value + 1)} />
      {data && <>
        <div className={`admin-canary-banner ${degraded ? 'degraded' : 'healthy'}`} role="status">
          <strong>{degraded ? 'Watchdog needs attention.' : 'Watchdog is current.'}</strong>
          <span>{!data.watchdog.running ? 'The watchdog is stopped. ' : ''}
            {stale ? 'No recent completed sweep. ' : ''}Last sweep: {dateTime(data.watchdog.lastSweepAt || undefined)}.</span>
        </div>
        {data.watchdog.lastSweepError && <Notice danger>Last sweep failed: {data.watchdog.lastSweepError}</Notice>}
        <div className="admin-metrics admin-canary-metrics">
          <Metric label="Flows started" value={data.funnel.started.toLocaleString()} detail={`${data.funnel.processing} still processing`} />
          <Metric label="Flows completed" value={data.funnel.completed.toLocaleString()} detail="Completion recorded" />
          <Metric label="Flows failed" value={data.funnel.failed.toLocaleString()} detail={`${data.funnel.cancelled} cancelled`} />
          <Metric label="Processing p95" value={duration(data.funnel.p95ProcessingMs)} detail={`p50: ${duration(data.funnel.p50ProcessingMs)} · ${data.funnel.processingSampleCount} completed or failed duration samples`} />
        </div>
        {!!data.flows?.items.length && <>
          <h3 className="admin-canary-subheading">Recent flow timings</h3>
          <p className="admin-canary-note">Showing {data.flows.items.length} of {data.flows.total} flows. Active durations are elapsed so far.</p>
          <div className="admin-table-wrap"><table className="admin-table">
            <thead><tr><th scope="col">Flow</th><th scope="col">State</th><th scope="col">Total / processing</th><th scope="col">Stages</th></tr></thead>
            <tbody>{data.flows.items.map(flow => <tr key={flow.flowId}>
              <td>{humanize(flow.kind)}<small>{flow.flowId}</small></td>
              <td><Status value={flow.state} /></td>
              <td>{duration(flow.durationMs)} / {duration(flow.processingMs)}</td>
              <td>{Object.entries(flow.stageDurationsMs).map(([step, elapsed]) => <small key={step}>{humanize(step)}: {duration(elapsed)}</small>)}</td>
            </tr>)}</tbody>
          </table></div>
        </>}
        <h3 className="admin-canary-subheading">Recent flow alerts</h3>
        {data.alerts.items.length ? <>
          <p className="admin-canary-note">Showing {data.alerts.items.length} of {data.alerts.total} alerts detected in this window.</p>
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead><tr><th scope="col">Detected</th><th scope="col">Flow and step</th><th scope="col">Reason</th><th scope="col">Context</th></tr></thead>
              <tbody>{data.alerts.items.map(alert => <tr key={alert.eventId}>
                <td>{dateTime(alert.timestamp)}<small>Age at detection: {duration(alert.ageMs)}</small></td>
                <td>{humanize(alert.kind)}<small>{humanize(alert.step)} · <Status value={alert.state} /></small></td>
                <td>{humanize(alert.reason)}<small>{alert.resolvedAt ? `Resolved ${dateTime(alert.resolvedAt)}` : 'No resolution recorded'}</small></td>
                <td><span className="admin-canary-sha">Flow: {alert.flowId}</span>
                  <small>Bill: {alert.billId || 'Not assigned'} · User: {alert.userId || 'Unknown'}</small>
                  <small>Version: {alert.version || 'Unknown'}</small></td>
              </tr>)}</tbody>
            </table>
          </div>
        </> : <p className="admin-canary-note">No flow alerts recorded in this window.</p>}
        <p className="admin-canary-note">
          Flows started {dateTime(data.window.from)} – {dateTime(data.window.to)}. Snapshot: {dateTime(data.asOf)}.
          {' '}Counts reflect their latest recorded state; missing duration samples are excluded from p95.
        </p>
      </>}
    </section>
  );
}

function Metric({ label, value, detail }: { label: string; value: string; detail: string }) {
  return <div className="admin-metric"><div className="admin-metric-heading">{label}</div><strong>{value}</strong><p>{detail}</p></div>;
}
